// Variants and modifier groups (platform target model §7.1, §7.2; legacy `product_prices`, `modifiers`, `modifier_items`,
// `modifier_product` and the five `modifier_override*` tables). A modifier group holds its options; a product attaches
// groups in order and may customize a group for itself or for some of its variants. Customizations are sparse: only
// the rules they set differ from the group, so an edit to the group still reaches every product that left a rule alone
// (target model §7.2, owner sign-off O-5). `core.catalog.resolveModifierGroup` is the one resolver.
import { z } from 'zod';
import { defineCommand, defineQuery } from '../../define/operations.ts';
import { LocationId, ModifierGroupId, ModifierOptionId, Money, ProductId, VariantId } from '../../primitives/codecs.ts';
import { CloudAuthoritative, ConfigFieldsWritten, ProposalRequired } from '../config/index.ts';
import { CatalogEntityNotFound, CatalogInvalid } from './errors.ts';

const Name = z.string().trim().min(1).max(120);
const SortKey = z.string().min(1).max(64);

/** A product's price row (legacy `product_prices`): a size or a unit. A product with no variants sells at its own price. */
export const VariantFields = z
  .object({
    productId: ProductId,
    name: z.string().trim().max(120),
    price: Money,
    /** Barcode / SKU the register's scanner looks up (legacy `product_prices.sku`). */
    sku: z.string().trim().max(64).nullable(),
    sortKey: SortKey,
    /** Preselected on the product card (legacy `products.default_price_id`). */
    isDefault: z.boolean(),
  })
  .meta({ id: 'VariantFields' });

export const VariantView = VariantFields.extend({ variantId: VariantId, locationId: LocationId.nullable(), version: z.int().nonnegative() })
  .meta({ id: 'VariantView' });

/** Conversational ordering buttons, in the register's order (Add, Extra, Lite, Side, Tossed, Only, No). */
export const ConversationButton = z.enum(['add', 'extra', 'lite', 'side', 'tossed', 'only', 'no']).meta({ id: 'ConversationButton' });

/**
 * A group's selection rules and presentation (legacy `modifiers` columns). `maximumUnique` and `maximumQuantity` are 0 for
 * "No Max". `minimum` only binds a required group.
 */
export const ModifierRules = z
  .object({
    posTitle: z.string().trim().max(120).nullable(),
    onlineTitle: z.string().trim().max(120).nullable(),
    showNameOnKitchenTicket: z.boolean(),
    required: z.boolean(),
    minimum: z.int().min(0).max(99),
    /** Legacy `maximum_unique_modifiers`: distinct options per side. */
    maximumUnique: z.int().min(0).max(99),
    /** Legacy `maximum`: options counted with their quantity. */
    maximumQuantity: z.int().min(0).max(999),
    allowMultipleQuantity: z.boolean(),
    /** Where the quantity stepper shows (legacy `quantity_selector_location`). */
    quantitySelector: z.enum(['modifier_set', 'individual', 'both']),
    defaultQuantity: z.int().min(1).max(99),
    conversational: z.boolean(),
    conversationButtons: z.array(ConversationButton).max(7),
    /** After a tap: reset the buttons, keep them, or reselect Add (legacy `convo_button_status`). */
    conversationReset: z.enum(['always', 'never', 'add']),
    splittable: z.boolean(),
    splitText: z.enum(['Side', 'Half']),
  })
  .meta({ id: 'ModifierRules' });

export const ModifierOption = z
  .object({
    optionId: ModifierOptionId,
    name: Name,
    kitchenName: z.string().trim().max(120).nullable(),
    /** Signed: legacy sells negative modifiers ("No Cheese −$0.50"). */
    price: Money,
    visibleOnPos: z.boolean(),
    visibleOnline: z.boolean(),
    sortKey: SortKey,
  })
  .meta({ id: 'ModifierOption' });

export const ModifierGroupFields = ModifierRules.extend({ name: Name, options: z.array(ModifierOption).max(200) }).meta({ id: 'ModifierGroupFields' });

export const ModifierGroupView = ModifierGroupFields.extend({
  modifierGroupId: ModifierGroupId, locationId: LocationId.nullable(), version: z.int().nonnegative(),
}).meta({ id: 'ModifierGroupView' });

/** A product's or variant's customization of a group (legacy "Customize Modifier Set" / "Create Variant Override"). */
export const ModifierCustomization = ModifierRules.partial()
  .extend({
    /** Preselected and free (legacy `modifier_override_item_includes`); only with the setting `use_included_modifiers`. */
    includedOptionIds: z.array(ModifierOptionId).max(200).optional(),
    /** Hidden on this product (legacy `modifier_override_item_omits`). */
    omittedOptionIds: z.array(ModifierOptionId).max(200).optional(),
    /** Option prices on this product (legacy `modifier_override_item_prices`). */
    optionPrices: z.array(z.object({ optionId: ModifierOptionId, price: Money })).max(200).optional(),
  })
  .meta({ id: 'ModifierCustomization' });

export const ProductModifierGroup = z
  .object({
    modifierGroupId: ModifierGroupId,
    visibleOnPos: z.boolean(),
    visibleOnline: z.boolean(),
    /** Applies to every variant unless a variant customization covers it. */
    customization: ModifierCustomization.nullable(),
    variantCustomizations: z.array(z.object({ variantIds: z.array(VariantId).min(1).max(50) }).extend(ModifierCustomization.shape)).max(50),
  })
  .meta({ id: 'ProductModifierGroup' });

const configErrors = [CatalogInvalid, CloudAuthoritative, ProposalRequired, CatalogEntityNotFound] as const;
const common = { availability: 'location', origin: 'any', emits: [ConfigFieldsWritten], errors: configErrors, dryRun: true } as const;

export const UpsertVariant = defineCommand({
  ...common,
  name: 'catalog.upsertVariant',
  summary: "Create or change one of a product's variants (size, unit): its name, price, SKU, order or default flag.",
  input: z.object({ variantId: VariantId, locationId: LocationId, shared: z.boolean().optional(), changes: VariantFields.partial() }),
  output: VariantView,
  permission: 'items.manage',
  risk: 'medium',
  agent: { expose: true, surfaces: ['backoffice.assistant', 'staff.onboarding'] },
  legacy: ['POST /v1/products', 'PUT /v1/products/{id}'],
});

export const DeleteVariant = defineCommand({
  ...common,
  name: 'catalog.deleteVariant',
  summary: 'Delete a variant.',
  input: z.object({ variantId: VariantId, locationId: LocationId }),
  output: z.object({ id: VariantId, deleted: z.literal(true) }),
  permission: 'items.manage',
  risk: 'medium',
  agent: { expose: true, surfaces: ['staff.onboarding'] },
  legacy: ['PUT /v1/products/{id}'],
});

export const UpsertModifierGroup = defineCommand({
  ...common,
  name: 'catalog.upsertModifierGroup',
  // E-09: legacy's Modifiers page needed Manage Modifiers and its API Manage Items; a back-office session needs both.
  backofficeAlso: ['modifiers.manage'],
  summary: 'Create or change a modifier set: its rules, presentation and modifiers.',
  input: z.object({ modifierGroupId: ModifierGroupId, locationId: LocationId, shared: z.boolean().optional(), changes: ModifierGroupFields.partial() }),
  output: ModifierGroupView,
  permission: 'items.manage',
  risk: 'medium',
  agent: { expose: true, surfaces: ['backoffice.assistant', 'staff.onboarding'] },
  legacy: ['POST /v1/modifiers', 'PUT /v1/modifiers/{id}', 'POST /v1/modifier-items', 'PUT /v1/modifier-items/{id}', 'DELETE /v1/modifier-items/{id}'],
});

export const DeleteModifierGroup = defineCommand({
  ...common,
  name: 'catalog.deleteModifierGroup',
  // E-09: legacy's Modifiers page needed Manage Modifiers and its API Manage Items; a back-office session needs both.
  backofficeAlso: ['modifiers.manage'],
  summary: 'Delete a modifier set; products that use it stop offering it.',
  input: z.object({ modifierGroupId: ModifierGroupId, locationId: LocationId }),
  output: z.object({ id: ModifierGroupId, deleted: z.literal(true) }),
  permission: 'items.manage',
  risk: 'medium',
  agent: { expose: true, surfaces: ['staff.onboarding'] },
  legacy: ['DELETE /v1/modifiers/{id}'],
});

const byLocation = z.object({ locationId: LocationId });
const listQuery = {
  availability: 'location', scope: 'location', errors: [], agent: { expose: true, surfaces: ['backoffice.assistant', 'staff.onboarding'] }, permission: { session: 'user' },
} as const;

export const ListVariants = defineQuery({
  ...listQuery, name: 'catalog.variants.list', summary: "Every variant of the location's products.", input: byLocation,
  output: z.object({ variants: z.array(VariantView) }), legacy: ['GET /v2/products'],
});

export const ListModifierGroups = defineQuery({
  ...listQuery, name: 'catalog.modifierGroups.list', summary: "The location's modifier sets with their modifiers.", input: byLocation,
  output: z.object({ modifierGroups: z.array(ModifierGroupView) }), legacy: ['GET /v1/modifiers'],
});

export const modifierCommands = [UpsertVariant, DeleteVariant, UpsertModifierGroup, DeleteModifierGroup] as const;
export const modifierQueries = [ListVariants, ListModifierGroups] as const;
